import { useCallback, useLayoutEffect, useRef } from 'react';

// Card names live in a fixed-height band that reserves room for two wrapped
// lines (one-line names sit vertically centered), so every card keeps the same
// dimensions. This hook only steps the text's font-size down when the name
// still cannot fit that box: a single word wider than the card, or a name that
// would need a third line. Attach the returned ref to the span inside the
// flex-centered band; the band keeps its own font-size, so paddings and the
// reserved height stay constant.
export default function useFitText(text, { minScale = 0.55 } = {}) {
  const textRef = useRef(null);

  const fit = useCallback(() => {
    const el = textRef.current;
    const container = el?.parentElement;
    if (!el || !container) return;

    el.style.fontSize = '';
    const containerStyles = window.getComputedStyle(container);
    const maxWidth = container.clientWidth
      - parseFloat(containerStyles.paddingLeft)
      - parseFloat(containerStyles.paddingRight);
    const maxHeight = container.clientHeight
      - parseFloat(containerStyles.paddingTop)
      - parseFloat(containerStyles.paddingBottom);
    // Non-positive box means not laid out yet (hidden tab, jsdom); skip.
    if (!(maxWidth > 0) || !(maxHeight > 0)) return;

    // offsetWidth/offsetHeight are layout units, immune to the reveal
    // animations' scale transforms (getBoundingClientRect is not).
    const fits = () => el.offsetWidth <= maxWidth + 1 && el.offsetHeight <= maxHeight + 1;
    if (fits()) return;

    const base = parseFloat(window.getComputedStyle(el).fontSize);
    if (!Number.isFinite(base) || base <= 0) return;

    let scale = 1;
    while (scale > minScale && !fits()) {
      scale = Math.max(scale - 0.05, minScale);
      el.style.fontSize = `${(base * scale).toFixed(2)}px`;
    }
  }, [minScale]);

  useLayoutEffect(() => {
    fit();
    const container = textRef.current?.parentElement;
    if (!container) return undefined;

    // Pitch slots get their size after mount (field measurements) and web
    // fonts swap in late; both change text metrics without necessarily
    // resizing the observed container, so a few delayed refits catch what
    // ResizeObserver cannot.
    let cancelled = false;
    const refit = () => {
      if (!cancelled) fit();
    };
    const retryTimers = [250, 1000, 2500].map((delay) => setTimeout(refit, delay));
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(refit);
    }

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(fit);
      observer.observe(container);
    }
    return () => {
      cancelled = true;
      retryTimers.forEach(clearTimeout);
      if (observer) observer.disconnect();
    };
  }, [fit, text]);

  return textRef;
}
